import {
    type AgentActor,
    agentActorSchema,
} from "@wags/agent-contracts/common.contract.ts";
import { z } from "zod";
import { authoredToolResultsSchema } from "@wags/agent-contracts/tool-result.contract.ts";

export const MAX_MESSAGE_TEXT_PART_LENGTH = 131_072;
export const MAX_PUBLIC_MESSAGE_BYTES = 700_000;

export const timestampSchema = z.string().datetime({ offset: true });
export const boundedJsonSchema = z
    .json()
    .refine(
        (value) =>
            new TextEncoder().encode(JSON.stringify(value)).byteLength <=
            1_000_000,
        { message: "JSON value exceeds one megabyte" },
    );

export const boundedJsonObjectSchema = z
    .record(z.string(), z.json())
    .refine(
        (value) =>
            new TextEncoder().encode(JSON.stringify(value)).byteLength <=
            1_000_000,
        { message: "JSON object exceeds one megabyte" },
    );

export const serializedAgentErrorSchema = z
    .object({
        code: z.string().min(1),
        message: z.string().min(1).max(16_384),
        retryable: z.boolean(),
        details: z.record(z.string(), z.json()).optional(),
    })
    .strict();

export { agentActorSchema };

const agentToolMessagePartSchema = z.preprocess(
    (value) => {
        if (
            typeof value !== "object" ||
            value === null ||
            !("data" in value) ||
            "metadata" in value
        ) {
            return value;
        }
        const { data, ...part } = value as Record<string, unknown>;
        return { ...part, metadata: { publicData: data } };
    },
    z
        .object({
            type: z.literal("tool"),
            toolCallId: z.string().min(1),
            toolId: z.string().min(1),
            state: z.enum(["input", "completed", "failed"]),
            metadata: z
                .object({
                    publicData: boundedJsonSchema.optional(),
                    authoredToolResults: authoredToolResultsSchema.optional(),
                })
                .strict()
                .optional(),
            input: boundedJsonSchema.optional(),
            output: boundedJsonSchema.optional(),
            error: boundedJsonSchema.optional(),
            text: z.string().max(MAX_MESSAGE_TEXT_PART_LENGTH).optional(),
        })
        .strict(),
);

export const agentMessagePartSchema = z.union([
    z
        .object({
            type: z.literal("text"),
            text: z.string().max(MAX_MESSAGE_TEXT_PART_LENGTH),
        })
        .strict(),
    agentToolMessagePartSchema,
    z
        .object({
            type: z.literal("data"),
            name: z.string().min(1).max(128),
            data: boundedJsonSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("reasoning"),
            text: z.string().max(MAX_MESSAGE_TEXT_PART_LENGTH),
        })
        .strict(),
    z
        .object({
            type: z.literal("file"),
            data: boundedJsonSchema,
            mediaType: z.string().min(1).max(256),
            filename: z.string().min(1).max(512).optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("reasoning-file"),
            data: boundedJsonSchema,
            mediaType: z.string().min(1).max(256),
            filename: z.string().min(1).max(512).optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("custom"),
            kind: z.string().min(1).max(256),
            data: boundedJsonSchema.optional(),
        })
        .strict(),
]);

export const agentMessageSchema = z
    .object({
        messageId: z.string().min(1),
        role: z.enum(["user", "assistant", "system"]),
        visibility: z.enum(["internal", "web"]).optional(),
        parts: z.array(agentMessagePartSchema).max(100),
        createdAt: timestampSchema,
        completionCursor: z.number().int().positive().optional(),
        firstTextCursor: z.number().int().positive().optional(),
        actor: agentActorSchema.optional(),
        interrupted: z.boolean().optional(),
        external: z.record(z.string(), boundedJsonSchema).optional(),
    })
    .strict();

export const agentUsageSchema = z
    .object({
        inputTokens: z.number().int().nonnegative().optional(),
        cachedInputTokens: z.number().int().nonnegative().optional(),
        cacheWriteInputTokens: z.number().int().nonnegative().optional(),
        outputTokens: z.number().int().nonnegative().optional(),
        reasoningOutputTokens: z.number().int().nonnegative().optional(),
        totalTokens: z.number().int().nonnegative().optional(),
        modelContextWindow: z.number().int().positive().optional(),
    })
    .strict();

export const agentOutputSchema = z
    .object({
        text: z.string().max(MAX_MESSAGE_TEXT_PART_LENGTH).optional(),
        structured: boundedJsonSchema.optional(),
    })
    .strict()
    .refine(
        (output) =>
            output.text !== undefined || output.structured !== undefined,
        {
            message: "Output must contain text or structured data",
        },
    );

export type { AgentActor };
export type AgentMessage = z.infer<typeof agentMessageSchema>;
export type AgentMessagePart = z.infer<typeof agentMessagePartSchema>;
export type AgentOutput = z.infer<typeof agentOutputSchema>;
export type AgentUsage = z.infer<typeof agentUsageSchema>;
export type SerializedAgentError = z.infer<typeof serializedAgentErrorSchema>;
